import api from './api';

export async function searchSuppliers(q) {
  const { data } = await api.get('/api/nova/supplier-search', { params: { q } });
  return data;
}

export async function searchProducts(q) {
  const { data } = await api.get('/api/nova/product-search', { params: { q } });
  return data;
}

export async function novaChatContext(message) {
  const { data } = await api.post('/api/nova/chat', { message });
  return data;
}

export async function novaChat({ messages, user, novaContext }) {
  const { data } = await api.post('/api/nova/chat', { messages, user, novaContext });
  return data;
}

const API_SCOPE = 'api://6069b616-c7f2-4978-9f9c-180ec12f1844/access_as_user';

async function getNovaToken(msalInstance) {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) throw new Error('No authenticated account');
  try {
    const resp = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account: accounts[0],
    });
    return resp.accessToken;
  } catch {
    const resp = await msalInstance.acquireTokenPopup({
      scopes: [API_SCOPE],
    });
    return resp.accessToken;
  }
}

export async function novaChatReport(msalInstance, message) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.post('/api/nova/chat-report', { message }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

export async function novaGenerateReport(msalInstance, { reportType, format, filters, language }) {
  const token = await getNovaToken(msalInstance);
  const body = { reportType, format, filters };
  if (language) body.language = language;
  // Send client local time for timestamp slides (dd/mm/yyyy - HH:mm)
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  body.clientTime = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} - ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const { data } = await api.post('/api/nova/report', body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

export async function novaGenerateReportAsync(msalInstance, { reportType, format, filters, language, style }) {
  const token = await getNovaToken(msalInstance);
  const body = { reportType, format, filters };
  if (language) body.language = language;
  if (style) body.style = style;
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  body.clientTime = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} - ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const { data } = await api.post('/api/nova/report/async', body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

export async function novaReportStatus(msalInstance, processId) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.get(`/api/nova/report/status/${processId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

export async function novaDownloadReport(msalInstance, reportId) {
  const token = await getNovaToken(msalInstance);
  const parts = reportId.split('/');
  const reportType = parts[1];
  const filename = parts.slice(2).join('/');
  const resp = await api.get(`/api/nova/outputs/${reportType}/${encodeURIComponent(filename)}/download`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });
  return resp.data;
}

// ── Market / Full Data Analysis reports ──

export async function novaStartMarketReport(msalInstance, { molecule, format, reportScope }) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.post('/api/nova/report/market', { molecule, format, reportScope }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

export async function novaMarketReportStatus(msalInstance, jobId) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.get(`/api/nova/report/market/${jobId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

// ── Chat session persistence ──

export async function novaSaveSession(msalInstance, session) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.put(`/api/nova/sessions/${session.sessionId}`, session, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

export async function novaListSessions(msalInstance) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.get('/api/nova/sessions', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data.sessions || [];
}

export async function novaGetSession(msalInstance, sessionId) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.get(`/api/nova/sessions/${sessionId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

export async function novaDeleteSession(msalInstance, sessionId) {
  const token = await getNovaToken(msalInstance);
  await api.delete(`/api/nova/sessions/${sessionId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function novaRenameSession(msalInstance, sessionId, title) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.patch(`/api/nova/sessions/${sessionId}`, { title }, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

// ── File Compare (Q1) ──

export async function novaFileCompareStart(msalInstance, file, supplierName, fileSupplierName) {
  const token = await getNovaToken(msalInstance);
  const fd = new FormData();
  fd.append('file', file);
  fd.append('supplier_name', supplierName);
  if (fileSupplierName) fd.append('file_supplier_name', fileSupplierName);
  const { data } = await api.post('/api/nova/file-compare', fd, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}

export async function novaFileCompareStatus(msalInstance, processId) {
  const token = await getNovaToken(msalInstance);
  const { data } = await api.get(`/api/nova/file-compare/status/${processId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data;
}
